import { Editor, useMonaco } from '@monaco-editor/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PiWarningCircleDuotone, PiXCircle } from 'react-icons/pi'
import { NodeProps } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeStepType } from '@ui/__generated__/graphql'
import { ChevronDownIcon } from '@ui/components/icons'
import datableTheme from '@ui/constants/editorThemes/datable.json'
// import dawnTheme from '@ui/constants/editorThemes/dawn.json'
// import githubLightTheme from '@ui/constants/editorThemes/githubLight.json'
import { NodeTypeEnum } from '@ui/interfaces'
import { usePipelineDataContext } from '@ui/providers/PipelineDataProvider'
import { useNodeAndEdgeStore, useNodeStepStore } from '@ui/stores/pipelinesWorkflow'
import { CodeStepConfig } from '@ui/stores/pipelinesWorkflow/NodeStepStore'
import { useDebounce } from '@uidotdev/usehooks'
import clsx from 'clsx'

interface CodeEditorProps {
  node: NodeProps
}

export const CodeEditor = ({ node }: CodeEditorProps) => {
  const editorContainerRef = useRef<HTMLDivElement>(null)

  const monaco = useMonaco()
  const { consoleLog, errorLog } = usePipelineDataContext()

  const { stepId, getConfig, setConfig } = useNodeStepStore(
    useShallow((state) => ({
      stepId: state.stepId,
      getConfig: state.getConfig,
      setConfig: state.setConfig,
    }))
  )

  const { currentNodeId, updateNodeStepConfig, updateNodeKeyValue } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      updateNodeStepConfig: state.updateNodeStepConfig,
      updateNodeKeyValue: state.updateNodeKeyValue,
    }))
  )

  const codeConfig = getConfig(stepId, NodeStepType.Code) as CodeStepConfig

  const [localCode, setLocalCode] = useState<string | null>(null)
  const [editorHeight, setEditorHeight] = useState(0)
  const [consoleLogOpen, setConsoleLogOpen] = useState(false)
  const [errorLogOpen, setErrorLogOpen] = useState(false)

  const debouncedLocalCode = useDebounce(localCode, 750)

  const handleCodeChange = (value: string | undefined) => {
    if (value) {
      setConfig(stepId, NodeStepType.Code, { code: value })
      setLocalCode(value)
    }
  }

  useEffect(() => {
    if (!monaco) return
    try {
      // @ts-expect-error monaco-editor/react doesn't export the type for defineTheme
      monaco.editor.defineTheme('datable', datableTheme)
    } catch (error) {
      console.error('Failed to load editor theme:', error)
    }
  }, [monaco])

  useEffect(() => {
    // Reset the local code if the stepId changes
    setLocalCode(null)

    if (debouncedLocalCode) {
      if (stepId) {
        updateNodeStepConfig(stepId, { code: debouncedLocalCode })
      } else {
        updateNodeKeyValue(currentNodeId, { code: debouncedLocalCode })
      }
    }
  }, [debouncedLocalCode, stepId, currentNodeId, updateNodeStepConfig, updateNodeKeyValue])

  useEffect(() => {
    const editorContainer = editorContainerRef.current
    if (!editorContainer) return
    setEditorHeight(editorContainer.clientHeight)
  }, [editorContainerRef])

  // Responsive editor
  // useEffect(() => {
  //   const editorContainer = editorContainerRef.current
  //   if (!editorContainer) return

  //   const resizeObserver = new ResizeObserver((entries) => {
  //     const containerHeight = entries[0].contentRect.height
  //     const containerWidth = entries[0].contentRect.width

  //     setEditorHeight(containerHeight)
  //     setEditorWidth(containerWidth)
  //   })
  //   resizeObserver.observe(editorContainer)

  //   return () => resizeObserver.disconnect()
  // }, [editorContainerRef, editorHeight, editorWidth])

  const code = useMemo(() => {
    if (node.type === NodeTypeEnum.CODE) {
      return node.data.code
    }
    return codeConfig.code
  }, [node.type, node.data.code, codeConfig.code])

  // Typescript validation for future use
  // useEffect(() => {
  //   if (!monaco) return

  // monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
  //   noSemanticValidation: true,
  //   noSyntaxValidation: true,
  //   // Ignore the error code 1108 which is the error code for the error "return of function outside of a body"
  //   diagnosticCodesToIgnore: [1108, 2304],
  // })

  // monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
  //   target: monaco.languages.typescript.ScriptTarget.ES2020,
  //   allowNonTsExtensions: true,
  //   moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
  //   module: monaco.languages.typescript.ModuleKind.CommonJS,
  //   noEmit: true,
  //   typeRoots: ['node_modules/@types'],
  // })

  // Add any custom JavaScript snippets or completions if desired
  // monaco.languages.registerCompletionItemProvider('javascript', {
  //   provideCompletionItems: () => {
  //     return {
  //       suggestions: [
  //         {
  //           label: 'console.log',
  //           kind: monaco.languages.CompletionItemKind.Function,
  //           insertText: 'console.log($1)',
  //           insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
  //         },
  //       ],
  //     }
  //   },
  // })
  // }, [monaco])

  return (
    <div className="flex size-full flex-col space-y-2 rounded-md" ref={editorContainerRef}>
      <Editor
        // Weird number because of the padding - the console height
        height={editorHeight * 0.7375}
        defaultLanguage="javascript"
        theme="datable"
        value={localCode ?? code}
        onChange={handleCodeChange}
        className="w-full rounded-md border border-gray-200 text-sm"
        options={{
          minimap: { enabled: false },
          fontSize: 12,
          lineNumbers: 'on',
          roundedSelection: false,
          scrollBeyondLastLine: false,
          readOnly: false,
          automaticLayout: true,
        }}
      />
      <div style={{ height: editorHeight * 0.25 }} className="w-full overflow-y-auto rounded-md border border-gray-200">
        <div className="sticky top-0 z-10 w-full border-b border-gray-200 bg-white p-2">
          <p className="text-sm font-medium">Console</p>
        </div>
        <div className="flex flex-col space-y-2 p-2">
          {/* Console log */}
          {consoleLog && (
            <div
              className={clsx(
                'relative cursor-pointer rounded-md bg-warning-100 p-2 transition-all duration-300 ease-in-out',
                consoleLogOpen ? 'h-40' : 'h-10'
              )}
              onClick={() => setConsoleLogOpen(!consoleLogOpen)}>
              <ChevronDownIcon
                className={clsx(
                  'absolute right-2 top-2 stroke-gray-400 transition-all duration-300 ease-in-out',
                  consoleLogOpen ? 'rotate-180' : 'rotate-0'
                )}
              />
              <div className="flex flex-row items-center space-x-2">
                <PiWarningCircleDuotone className="size-4 text-warning-500" />
                <p className={clsx('pr-6 text-sm', consoleLogOpen ? 'whitespace-pre-wrap break-words' : 'truncate')}>
                  {consoleLog}
                </p>
              </div>
            </div>
          )}
          {/* Error log */}
          {errorLog && (
            <div
              className={clsx(
                'relative cursor-pointer rounded-md bg-negative-100 p-2 transition-all duration-300 ease-in-out',
                errorLogOpen ? 'h-40' : 'h-10'
              )}
              onClick={() => setErrorLogOpen(!errorLogOpen)}>
              <ChevronDownIcon
                className={clsx(
                  'absolute right-2 top-2 stroke-gray-400 transition-all duration-300 ease-in-out',
                  errorLogOpen ? 'rotate-180' : 'rotate-0'
                )}
              />
              <div className="flex flex-row items-center space-x-2">
                <PiXCircle className="size-4 text-negative-500" />
                <p className={clsx('pr-6 text-sm', errorLogOpen ? 'whitespace-pre-wrap break-words' : 'truncate')}>
                  {errorLog}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
